import { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import { BackendError, type BackendClient, type Session } from '@lumen/data';
import type { Platform } from '@lumen/platform';
import { installTokens, ToastHost } from '@lumen/ui';
import '@lumen/ui/base.css';
import './app.css';
import { AuthScreen, ForgotPasswordScreen, JoinScreen, ResetPasswordScreen, VerifyEmailScreen } from './AuthScreen';
import { Landing, type Downloads } from './Landing';
import { useRoute } from './router';

import { LAST_WS_KEY, SESSION_KEY, TOKEN_KEY } from './session-keys';
import { t } from '@lumen/platform';

// The app shell (editor, databases, …) and published pages load after the first screen (code splitting).
const WorkspaceRoot = lazy(() => import('./WorkspaceRoot'));
const PublicSite = lazy(() => import('./PublicSite').then((m) => ({ default: m.PublicSite })));

export interface LumenAppProps {
  platform: Platform;
  backend: BackendClient;
  /** WebSocket URL of the sync server, or null for local-only mode. */
  syncUrl: string | null;
  document: Document;
  /** Build-time native app links (P-625). The server's `/api/config` wins when it has them. */
  downloads?: Downloads;
}

export function LumenApp({ platform, backend, syncUrl, document, downloads }: LumenAppProps) {
  const route = useRoute(platform.navigation);
  const [session, setSession] = useState<Session | null>(() => {
    const raw = platform.storage.get(SESSION_KEY);
    try {
      return raw ? (JSON.parse(raw) as Session) : null;
    } catch {
      return null;
    }
  });
  const [checked, setChecked] = useState(false);

  useEffect(() => installTokens(document), [document]);

  // The iOS app shows the sign-in screen full screen, without its native chrome, while nobody is signed in.
  const signedOut = !session && (checked || !platform.storage.get(TOKEN_KEY));
  useEffect(() => {
    if (signedOut) platform.native?.post({ type: 'ready', signedIn: false, workspace: null });
  }, [signedOut, platform]);

  const saveSession = useCallback(
    (s: Session | null) => {
      if (s) {
        platform.storage.set(TOKEN_KEY, s.token);
        platform.storage.set(SESSION_KEY, JSON.stringify(s));
      } else {
        platform.storage.remove(TOKEN_KEY);
        platform.storage.remove(SESSION_KEY);
      }
      setSession(s);
    },
    [platform],
  );

  // Revalidate the stored session; keep the cached one when offline.
  useEffect(() => {
    const token = platform.storage.get(TOKEN_KEY);
    if (!token) {
      setChecked(true);
      return;
    }
    backend
      .me(token)
      .then((s) => saveSession(s))
      .catch((e) => {
        if (e instanceof BackendError && e.status === 401) saveSession(null);
      })
      .finally(() => setChecked(true));
  }, [backend, platform, saveSession]);

  if (route.name === 'site') {
    return (
      <Suspense fallback={<div className="lm-loading" />}>
        <PublicSite platform={platform} backend={backend} syncUrl={syncUrl} wsSlug={route.ws} pageId={route.pageId} document={document} />
      </Suspense>
    );
  }
  if (route.name === 'verify') return <VerifyEmailScreen token={route.token} platform={platform} backend={backend} onDone={() => platform.navigation.replace('/')} />;
  if (route.name === 'reset') {
    return (
      <ResetPasswordScreen
        token={route.token}
        platform={platform}
        backend={backend}
        onSession={(s) => {
          saveSession(s);
          platform.navigation.replace(`/${s.workspaces[0]?.slug ?? ''}`);
        }}
      />
    );
  }
  if (route.name === 'forgot' && !session) return <ForgotPasswordScreen platform={platform} backend={backend} />;
  if (route.name === 'join' && (session || checked || !platform.storage.get(TOKEN_KEY))) {
    return <JoinScreen token={route.token} platform={platform} backend={backend} session={session} onSession={saveSession} onJoined={(slug) => platform.navigation.replace(`/${slug}`)} />;
  }

  if (!session) {
    if (!checked && platform.storage.get(TOKEN_KEY)) return <div className="lm-loading" />;
    // Without a session, `/` is the public landing page (P-620); every other route keeps its screen. The Mac and iOS
    // apps sign in there instead: the website's page (with its download buttons) has no place in them (P-740).
    if (route.name === 'root' && !platform.native) return <Landing platform={platform} backend={backend} document={document} downloads={downloads} />;
    return (
      <>
        <AuthScreen
          mode={route.name === 'signup' ? 'signup' : 'login'}
          initialEmail={route.name === 'signup' ? route.email : null}
          platform={platform}
          backend={backend}
          onSession={(s) => {
            saveSession(s);
            platform.navigation.replace(`/${s.workspaces[0]?.slug ?? ''}`);
          }}
        />
        <ToastHost />
      </>
    );
  }

  const wsSlug = route.name === 'workspace' || route.name === 'home' || route.name === 'universe' || route.name === 'page' || route.name === 'notfound' ? route.ws : null;
  const info = session.workspaces.find((w) => w.slug === wsSlug) ?? session.workspaces.find((w) => w.slug === platform.storage.get(LAST_WS_KEY)) ?? session.workspaces[0];
  if (!info) return <NoWorkspace backend={backend} session={session} onSession={saveSession} />;
  if (route.name === 'login' || route.name === 'signup' || route.name === 'forgot' || route.name === 'root' || (wsSlug && wsSlug !== info.slug)) {
    queueMicrotask(() => platform.navigation.replace(`/${info.slug}`));
    return <div className="lm-loading" />;
  }

  return (
    <Suspense fallback={<div className="lm-loading" data-testid="loading" />}>
      <WorkspaceRoot
        key={`${session.user.id}:${info.id}:${info.role}:${info.slug}`}
        platform={platform}
        backend={backend}
        syncUrl={syncUrl}
        session={session}
        workspaceId={info.id}
        document={document}
        onSignOut={async () => {
          try {
            await backend.signOut(session.token);
          } catch {
            /* already signed out or offline */
          }
          // With a server, signing out removes the local copy; without one it is the only copy.
          if (!backend.localOnly) await platform.persistence.clearAll();
          saveSession(null);
          platform.navigation.replace('/login');
        }}
        onSession={saveSession}
      />
    </Suspense>
  );
}

/** Signed in without any workspace (all were deleted or left): create one. */
function NoWorkspace({ backend, session, onSession }: { backend: BackendClient; session: Session; onSession: (s: Session) => void }) {
  const [name, setName] = useState(`${session.user.name}'s Lumen`);
  return (
    <div className="lm-empty-state" data-testid="no-workspace">
      <h1>{t('Create a workspace')}</h1>
      <p>{t('You are not a member of any workspace.')}</p>
      <form
        className="lm-settings-inline"
        onSubmit={(e) => {
          e.preventDefault();
          if (!name.trim()) return;
          void backend.createWorkspace(session.token, name.trim()).then(async () => onSession(await backend.me(session.token)));
        }}
      >
        <input className="lm-input" value={name} onChange={(e) => setName(e.target.value)} data-testid="no-workspace-name" />
        <button className="lm-btn lm-btn-primary" type="submit" data-testid="no-workspace-create">
          {t('Create workspace')}
        </button>
      </form>
    </div>
  );
}
